import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, } from 'react';
import { rowClickOpens } from './rowClick.js';
import styles from './lists.module.css';
/**
 * Said once, on entering the table, rather than repeated on every row. Only
 * present when the rows actually open something.
 */
const INTERACTIVE_HINT = 'Rows in this table open a detail view: press Enter or Space on a row.';
/**
 * Row counts at or under this render exactly as before — no scroll wrapper,
 * no spacer rows, every row a real `<tr>`. This covers essentially every
 * existing caller (settings tables, program lists, a supervisor's own team) so
 * the windowing path is exercised only by the lists it was built for.
 */
const VIRTUALIZE_THRESHOLD = 150;
/**
 * A per-row estimate, not a measurement. Every row in this table renders the
 * same padding and a single line of 13.5px text (`lists.module.css`'s
 * `.table tbody td`: 12px top+bottom padding, a 1px bottom border, ~16px of
 * text) — 12 + 12 + 16 + 1 = 41px, rounded up for breathing room. A column
 * whose `render()` wraps text across multiple lines would make this estimate
 * conservative (spacer rows slightly taller than the rows they stand in for,
 * which oversizes the scrollbar a little) rather than wrong in the direction
 * that would clip content.
 */
const ROW_HEIGHT_PX = 44;
/** Fixed, not measured: see the note on `visibleCount` below for why. */
const VIEWPORT_HEIGHT_PX = 640;
/** Extra rows rendered past each edge of the viewport, so a fast scroll or a
 * Tab press past the last mounted row doesn't visibly pop content in. */
const OVERSCAN_ROWS = 8;
export function TableCard({ columns, rows, rowKey, onRowOpen, selectedId, caption, }) {
    const interactive = Boolean(onRowOpen);
    const virtualized = rows.length > VIRTUALIZE_THRESHOLD;
    const [scrollTop, setScrollTop] = useState(0);
    /**
     * The window is derived from a FIXED viewport height, not a measured one
     * (`ref.current.clientHeight`). jsdom (and a hidden tab, and a not-yet-laid-
     * out first paint) reports 0 for that, which would render zero rows rather
     * than a safe default — exactly the wrong failure mode for a list. A fixed
     * constant makes the window size deterministic and testable, at the cost of
     * a viewport that doesn't adapt to the actual card height on screen; the
     * card's own `max-height` (set from the same constant, `lists.module.css`)
     * keeps the two in agreement.
     */
    const { startIndex, endIndex } = useMemo(() => {
        if (!virtualized)
            return { startIndex: 0, endIndex: rows.length };
        const first = Math.floor(scrollTop / ROW_HEIGHT_PX) - OVERSCAN_ROWS;
        const visibleRows = Math.ceil(VIEWPORT_HEIGHT_PX / ROW_HEIGHT_PX);
        const last = first + visibleRows + OVERSCAN_ROWS * 2;
        return {
            startIndex: Math.max(0, first),
            endIndex: Math.min(rows.length, Math.max(0, last)),
        };
    }, [virtualized, scrollTop, rows.length]);
    const windowRows = virtualized ? rows.slice(startIndex, endIndex) : rows;
    const topSpacerPx = virtualized ? startIndex * ROW_HEIGHT_PX : 0;
    const bottomSpacerPx = virtualized ? (rows.length - endIndex) * ROW_HEIGHT_PX : 0;
    const handleScroll = (event) => {
        setScrollTop(event.currentTarget.scrollTop);
    };
    /**
     * Enter opens, as it does for a link; Space opens and swallows the page
     * scroll, as it does for a button. A key pressed inside a cell's own control
     * belongs to that control — opening the row underneath it as well would fire
     * two actions from one keystroke.
     */
    const handleKeyDown = (row) => (event) => {
        if (!onRowOpen)
            return;
        if (event.target !== event.currentTarget)
            return;
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar')
            return;
        event.preventDefault();
        onRowOpen(row);
    };
    /**
     * The same rule for a mouse, which did not have one.
     *
     * The keyboard handler's `event.target !== event.currentTarget` cannot be
     * reused here: a click's target is the element under the pointer — a `<td>`,
     * or a `<span>` inside it — and never the `<tr>`, so that test would suppress
     * every row click there is. `rowClickOpens` asks the question a click has to
     * ask instead: is anything between the pointer and the row a control in its
     * own right. See rowClick.ts for why this is a guard rather than a fix.
     */
    const handleClick = (row) => (event) => {
        if (!onRowOpen)
            return;
        if (!rowClickOpens(event.target, event.currentTarget))
            return;
        onRowOpen(row);
    };
    return (_jsx("div", { className: styles.tableCard, children: _jsx("div", { className: virtualized ? `${styles.scroll} ${styles.scrollVirtual}` : styles.scroll, onScroll: virtualized ? handleScroll : undefined, "data-virtualized": virtualized || undefined, children: _jsxs("table", { className: styles.table, children: [caption || interactive ? (_jsxs("caption", { className: caption ? styles.caption : undefined, children: [caption, interactive ? _jsx("span", { className: styles.srOnly, children: INTERACTIVE_HINT }) : null] })) : null, _jsx("thead", { children: _jsx("tr", { children: columns.map((c) => (_jsx("th", { scope: "col", children: c.header }, c.key))) }) }), _jsxs("tbody", { children: [topSpacerPx > 0 ? (_jsx("tr", { "aria-hidden": "true", "data-spacer": "top", children: _jsx("td", { colSpan: columns.length, style: { height: topSpacerPx, padding: 0, border: 'none' } }) })) : null, windowRows.map((row) => {
                                const id = rowKey(row);
                                return (_jsx("tr", { className: interactive ? `${styles.row} ${styles.rowInteractive}` : styles.row, "data-selected": selectedId === id, "aria-current": selectedId === id ? 'true' : undefined, onClick: onRowOpen ? handleClick(row) : undefined, onKeyDown: interactive ? handleKeyDown(row) : undefined, tabIndex: interactive ? 0 : undefined, children: columns.map((c) => (_jsx("td", { className: c.numeric ? styles.num : undefined, children: c.render(row) }, c.key))) }, id));
                            }), bottomSpacerPx > 0 ? (_jsx("tr", { "aria-hidden": "true", "data-spacer": "bottom", children: _jsx("td", { colSpan: columns.length, style: { height: bottomSpacerPx, padding: 0, border: 'none' } }) })) : null] })] }) }) }));
}
//# sourceMappingURL=TableCard.js.map